'use client';

import {
	PerpPosition,
	SpotBalanceType,
	User,
	UserStatsAccount,
	positionIsAvailable,
} from '@drift-labs/sdk';
import { ACTION_HELPERS, COMMON_UI_UTILS, ENUM_UTILS } from '@drift/common';
import { useMemo, useState } from 'react';
import useAccountSpotBalances from 'src/hooks/useAccountBankBalances';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useTargetAccountData from '../../hooks/useTargetAccountData';
import useInterval from '../../hooks/useInterval';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { WithChildren } from 'src/utils/UtilityTypes';
import { useDeleteUser } from 'src/hooks/driftActions/user/useDeleteUser';

export interface DeleteSubaccountModalProps {
	subaccountId: number;
}

const DeleteSubaccountModal = ({
	subaccountId,
}: DeleteSubaccountModalProps) => {
	const [isDeleting, setIsDeleting] = useState(false);
	const getDriftStore = useDriftStore((s) => s.get);
	const { hide } = useModal();
	const { deleteUser } = useDeleteUser();

	const currentAuthority = useDriftStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const accountsState = useDriftAccountStore((s) => s.get)();
	const perpMarketsLookup = usePerpMarketsLookup();

	const userKey = currentAuthority
		? COMMON_UI_UTILS.getUserKey(subaccountId, currentAuthority)
		: '';

	const currentAccount = accountsState?.accounts?.[userKey];

	const [latestSlot, setLatestSlot] = useState(0);

	useInterval(() => {
		getDriftStore()
			.connection.current?.getSlot()
			.then((slot) => {
				if (slot) {
					setLatestSlot(slot);
				}
			});
	}, 5000);

	const handleDelete = async () => {
		setIsDeleting(true);
		const userAccountToDelete = currentAccount?.client;
		await deleteUser(userAccountToDelete as User);
		setIsDeleting(false);

		hide();
	};

	const spotBalances = useAccountSpotBalances(userKey);

	const nonZeroBalances = useMemo(() => {
		return spotBalances?.filter((b) => !b.balance.eqZero()) ?? [];
	}, [spotBalances]);

	const depositBalances = useMemo(() => {
		return nonZeroBalances?.filter((b) =>
			ENUM_UTILS.match(b.balanceType, SpotBalanceType.DEPOSIT)
		);
	}, [nonZeroBalances]);

	const borrowBalances = useMemo(() => {
		return nonZeroBalances?.filter((b) =>
			ENUM_UTILS.match(b.balanceType, SpotBalanceType.BORROW)
		);
	}, [nonZeroBalances]);

	const openPerpPositionsAndOrders = useMemo(() => {
		if (!currentAccount) {
			return [];
		}

		const userAccount = currentAccount?.client?.getUserAccount();

		if (!userAccount || !userAccount?.perpPositions) {
			return [];
		}

		const nonZeroPositionSet = new Map<number, PerpPosition>();

		userAccount.perpPositions.forEach((position) => {
			// position is available returns TRUE when the position is blank|empty
			if (!positionIsAvailable(position)) {
				nonZeroPositionSet.set(position.marketIndex, position);
			}
		});

		return Array.from(nonZeroPositionSet.values());
	}, [currentAccount]);

	const canBeDeletedStatus = useMemo(() => {
		if (!currentAccount) return 'no';

		const user = currentAccount.client;
		const userStatsAccount = getDriftStore().userStatsAccount;
		return ACTION_HELPERS.ACCOUNT_DELETION_HELPERS.getAccountCanBeDeletedInstantly(
			user as User,
			userStatsAccount as UserStatsAccount,
			latestSlot
		);
	}, [currentAccount, latestSlot]);

	const canBeDeleted =
		canBeDeletedStatus === 'yes' ||
		canBeDeletedStatus === 'yes-after-making-idle';

	const canDeleteAfterWaiting = canBeDeletedStatus === 'no-wait-for-idle';

	const idleWaitTimeEstimateMinutes = useMemo(() => {
		if (!currentAccount || latestSlot === 0) return 0;

		if (canBeDeletedStatus === 'no-wait-for-idle') {
			return ACTION_HELPERS.ACCOUNT_DELETION_HELPERS.getIdleWaitTimeMinutes(
				currentAccount.client as User,
				latestSlot
			);
		}

		return 0; // Don't care otherwise
	}, [currentAccount, canBeDeletedStatus, latestSlot]);

	const selectedSubAccount = useTargetAccountData(userKey);

	if (!selectedSubAccount || !userKey) {
		return null;
	}

	const content = (() => {
		if (canBeDeleted) {
			return 'Deleting this account will refund some SOL to your wallet.';
		}

		if (canDeleteAfterWaiting) {
			return (
				<>
					<p>
						Drift is experiencing an influx of new accounts being created and
						has implemented a temporary cool down period for account deletions.
					</p>
					<p>
						Please note that any account activity, like depositing or trading,
						will reset the cool down period for the account.
					</p>

					<p className="flex items-center gap-x-xs text-star-yellow">
						<span>Estimated remaining cool down time:</span>
						{idleWaitTimeEstimateMinutes === 0 ? (
							<SkeletonValuePlaceholder loading className="h-4 w-6" />
						) : (
							<span>{idleWaitTimeEstimateMinutes} minutes.</span>
						)}
					</p>
				</>
			);
		}

		return (
			<>
				<InlineAlert variant="info" title="Account cannot be deleted" />

				{openPerpPositionsAndOrders.length > 0 && (
					<OutstandingActionsSection title="You need to close all PERP positions and orders">
						{openPerpPositionsAndOrders.map((p) => (
							<span key={p.marketIndex}>
								{perpMarketsLookup[p.marketIndex].symbol}
							</span>
						))}
					</OutstandingActionsSection>
				)}
				{depositBalances?.length > 0 && (
					<OutstandingActionsSection title="You need to withdraw all deposits and close all borrows">
						{depositBalances.map((b) => (
							<span key={`${b.asset.symbol}-deposit`}>{b.asset.symbol}</span>
						))}
						{borrowBalances.map((b) => (
							<span key={`${b.asset.symbol}-borrow`}>
								{b.asset.symbol} Borrow
							</span>
						))}
					</OutstandingActionsSection>
				)}
			</>
		);
	})();

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="delete-subaccount-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="delete-subaccount-modal-close">
				Delete {selectedSubAccount.name}
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>{content}</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					variant={canBeDeleted ? 'negative' : 'secondary'}
					className="w-full"
					onClick={canBeDeleted ? handleDelete : hide}
					isLoading={isDeleting}
					dataTestId="delete-subaccount-modal-submit"
				>
					{canBeDeleted ? 'Delete' : 'Close'}
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

interface OutstandingActionsSectionProps extends WithChildren {
	title: string;
}

function OutstandingActionsSection({
	title,
	children,
}: OutstandingActionsSectionProps) {
	return (
		<div className="flex flex-col gap-y-md rounded-sm border border-stroke-divider bg-input-bg p-lg">
			<p className="typo-t4 text-text-default">{title}</p>
			<div className="flex flex-col gap-y-sm typo-b4">{children}</div>
		</div>
	);
}

export default DeleteSubaccountModal;
